/**
 * StorageService - Centralized localStorage management
 * 
 * Benefits:
 * - Type-safe get/set with JSON serialization
 * - Automatic error handling
 * - Fallback for Safari private browsing
 * - Consistent key naming
 * - TTL (time-to-live) support
 * - Namespace support for different features
 * 
 * Usage:
 * import { storage, createNamespacedStorage } from '../services/storageService';
 * 
 * // Basic usage
 * storage.set('user', { name: 'John', age: 30 });
 * const user = storage.get('user');
 * storage.remove('user');
 * 
 * // With TTL (expires after 1 hour)
 * storage.set('cache', data, { ttl: 3600000 });
 * 
 * // Namespaced storage (for feature isolation)
 * const llmStorage = createNamespacedStorage('llm_generator');
 * llmStorage.set('settings', { provider: 'openai' });
 */

import { Logger } from '../utils/logger';

const log = Logger.create('StorageService');

// ============================================================================
// Storage Detection & Fallback
// ============================================================================

/**
 * In-memory fallback storage for when localStorage is unavailable
 * (e.g., Safari private browsing, storage quota exceeded)
 */
const memoryStorage = new Map();

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable() {
  try {
    const testKey = '__storage_test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    log.warn('localStorage not available, using memory fallback');
    return false;
  }
}

const storageAvailable = typeof window !== 'undefined' && isLocalStorageAvailable();

// ============================================================================
// Core Storage Functions
// ============================================================================

/**
 * Storage wrapper with JSON serialization and error handling
 */
export const storage = {
  /**
   * Get a value from storage
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if key doesn't exist or is expired
   * @returns {any} The stored value or defaultValue
   */
  get(key, defaultValue = null) {
    try {
      const item = storageAvailable 
        ? window.localStorage.getItem(key)
        : memoryStorage.get(key);

      if (item === null || item === undefined) {
        return defaultValue;
      }

      const parsed = JSON.parse(item);

      // Check TTL if present
      if (parsed && typeof parsed === 'object' && parsed.__ttl__) {
        if (Date.now() > parsed.__ttl__) {
          // Expired - remove and return default
          this.remove(key);
          log.debug(`Storage key "${key}" expired, removed`);
          return defaultValue;
        }
        return parsed.__value__;
      }

      return parsed;
    } catch (error) {
      log.warn(`Error reading storage key "${key}"`, error);
      return defaultValue;
    }
  },

  /**
   * Set a value in storage
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be JSON serialized)
   * @param {Object} options - Options
   * @param {number} options.ttl - Time-to-live in milliseconds
   * @returns {boolean} Success status
   */
  set(key, value, options = {}) {
    try {
      const { ttl } = options;

      let dataToStore = value;

      // Wrap with TTL if specified
      if (ttl && ttl > 0) {
        dataToStore = {
          __value__: value,
          __ttl__: Date.now() + ttl
        };
      }

      const serialized = JSON.stringify(dataToStore);

      if (storageAvailable) {
        window.localStorage.setItem(key, serialized);
      } else {
        memoryStorage.set(key, serialized);
      }

      log.debug(`Storage set: "${key}"`, { ttl: ttl || 'none' });
      return true;
    } catch (error) {
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        log.error('Storage quota exceeded', error);
        // Try to clear old data and retry
        this.clearExpired();
        try {
          const serialized = JSON.stringify(value);
          window.localStorage.setItem(key, serialized);
          return true;
        } catch (retryError) {
          log.error('Storage set failed after clearing', retryError);
        }
      }
      log.error(`Error setting storage key "${key}"`, error);
      return false;
    }
  },

  /**
   * Remove a key from storage
   * @param {string} key - Storage key to remove
   * @returns {boolean} Success status
   */
  remove(key) {
    try {
      if (storageAvailable) {
        window.localStorage.removeItem(key);
      } else {
        memoryStorage.delete(key);
      }
      log.debug(`Storage removed: "${key}"`);
      return true;
    } catch (error) {
      log.error(`Error removing storage key "${key}"`, error);
      return false;
    }
  },

  /**
   * Check if a key exists in storage
   * @param {string} key - Storage key
   * @returns {boolean} True if key exists and is not expired
   */
  has(key) {
    return this.get(key) !== null;
  },

  /**
   * Get all keys in storage (optionally filtered by prefix)
   * @param {string} prefix - Optional prefix filter
   * @returns {string[]} Array of keys
   */
  keys(prefix = '') {
    try {
      const allKeys = storageAvailable
        ? Object.keys(window.localStorage)
        : Array.from(memoryStorage.keys());

      return prefix 
        ? allKeys.filter(key => key.startsWith(prefix))
        : allKeys;
    } catch (error) {
      log.error('Error getting storage keys', error);
      return [];
    }
  },

  /**
   * Clear all keys with optional prefix filter
   * @param {string} prefix - Optional prefix filter (only clear keys with this prefix)
   */
  clear(prefix = '') {
    try {
      if (prefix) {
        const keysToRemove = this.keys(prefix);
        keysToRemove.forEach(key => this.remove(key));
        log.debug(`Storage cleared with prefix "${prefix}": ${keysToRemove.length} keys`);
      } else {
        if (storageAvailable) {
          window.localStorage.clear();
        } else {
          memoryStorage.clear();
        }
        log.debug('Storage cleared completely');
      }
    } catch (error) {
      log.error('Error clearing storage', error);
    }
  },

  /**
   * Clear all expired TTL entries
   */
  clearExpired() {
    const keys = this.keys();
    let clearedCount = 0;

    keys.forEach(key => {
      try {
        const item = storageAvailable 
          ? window.localStorage.getItem(key)
          : memoryStorage.get(key);

        if (item) {
          const parsed = JSON.parse(item);
          if (parsed && typeof parsed === 'object' && parsed.__ttl__) {
            if (Date.now() > parsed.__ttl__) {
              this.remove(key);
              clearedCount++;
            }
          }
        }
      } catch {
        // Ignore parse errors
      }
    });

    if (clearedCount > 0) {
      log.debug(`Cleared ${clearedCount} expired storage entries`);
    }
  },

  /**
   * Get storage usage info
   * @returns {Object} { used: number, available: number, percent: number }
   */
  getUsage() {
    if (!storageAvailable) {
      return { used: 0, available: 0, percent: 0 };
    }

    try {
      let total = 0;
      for (let key in window.localStorage) {
        if (window.localStorage.hasOwnProperty(key)) {
          total += (window.localStorage[key].length + key.length) * 2; // UTF-16 = 2 bytes
        }
      }

      // Most browsers have 5-10MB limit
      const availableEstimate = 5 * 1024 * 1024; // 5MB
      const percent = Math.round((total / availableEstimate) * 100);

      return {
        used: total,
        available: availableEstimate - total,
        percent: Math.min(percent, 100)
      };
    } catch {
      return { used: 0, available: 0, percent: 0 };
    }
  }
};

// ============================================================================
// Namespaced Storage
// ============================================================================

/**
 * Create a namespaced storage instance
 * All keys will be prefixed with the namespace
 * 
 * @param {string} namespace - Namespace prefix (e.g., 'llm_generator', 'quiz_participant')
 * @returns {Object} Storage object with get, set, remove, has, keys, clear methods
 */
export function createNamespacedStorage(namespace) {
  const prefix = `${namespace}_`;

  return {
    /**
     * Get a value from namespaced storage
     */
    get(key, defaultValue = null) {
      return storage.get(`${prefix}${key}`, defaultValue);
    },

    /**
     * Set a value in namespaced storage
     */
    set(key, value, options = {}) {
      return storage.set(`${prefix}${key}`, value, options);
    },

    /**
     * Remove a key from namespaced storage
     */
    remove(key) {
      return storage.remove(`${prefix}${key}`);
    },

    /**
     * Check if key exists in namespaced storage
     */
    has(key) {
      return storage.has(`${prefix}${key}`);
    },

    /**
     * Get all keys in this namespace
     */
    keys() {
      return storage.keys(prefix).map(k => k.slice(prefix.length));
    },

    /**
     * Clear all keys in this namespace
     */
    clear() {
      return storage.clear(prefix);
    },

    /**
     * Get the full prefixed key
     */
    getFullKey(key) {
      return `${prefix}${key}`;
    }
  };
}

// ============================================================================
// Pre-defined Namespaces (Convenience exports)
// ============================================================================

/**
 * Storage for LLM Generator settings
 */
export const llmStorage = createNamespacedStorage('llm');

/**
 * Storage for Quiz Participant data
 */
export const quizParticipantStorage = createNamespacedStorage('quiz_participant');

/**
 * Storage for Theme/UI preferences
 */
export const uiStorage = createNamespacedStorage('ui');

/**
 * Storage for Authentication-related data
 */
export const authStorage = createNamespacedStorage('auth');

/**
 * Storage for Template Editor
 */
export const templateStorage = createNamespacedStorage('template');

// ============================================================================
// Type Definitions (for documentation)
// ============================================================================

/**
 * @typedef {Object} StorageOptions
 * @property {number} [ttl] - Time-to-live in milliseconds
 */

/**
 * @typedef {Object} StorageUsage
 * @property {number} used - Bytes used
 * @property {number} available - Bytes available (estimate)
 * @property {number} percent - Percentage used
 */

// ============================================================================
// Export Default
// ============================================================================

export default storage;
